import type { TProduct } from "@lexicanon/shared"

/**
 * A question people would actually ask. Built from recent meeting titles.
 * Never from a meeting's open questions — those are the meeting's own
 * unresolved items, and the model usually can't answer them.
 */
export type TStarterQuestion =
  | { kind: "decided-this-week" }
  | { kind: "open-actions" }
  | { kind: "session-recap" }
  | { kind: "open-quests" }
  | { kind: "summarise"; title: string }
  | { kind: "came-up"; title: string }

/** How many recent titles to weave in (the latest, then one more). */
const MAX_TITLES = 2

/**
 * Stable id for a starter. Title-based questions include the title so two
 * meetings never collapse into one React key.
 */
export function starterKey(starter: TStarterQuestion): string {
  if (starter.kind === "summarise" || starter.kind === "came-up") {
    return `${starter.kind}:${starter.title}`
  }
  return starter.kind
}

/** Drop blanks and case-insensitive duplicates, keeping the first spelling. */
function cleanTitles(titles: readonly string[]): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of titles) {
    const title = raw.trim()
    if (title.length === 0) continue
    const key = title.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(title)
    if (out.length >= MAX_TITLES) break
  }
  return out
}

/**
 * Starter questions for an empty Ask palette or the Ask page.
 *
 * The two standing questions are things a person asks the product. The rest
 * name the user's own recent meetings. `recentTitles` is newest first.
 */
export function buildStarterQuestions(recentTitles: readonly string[], product: TProduct): TStarterQuestion[] {
  const questions: TStarterQuestion[] =
    product === "voxcanon"
      ? [{ kind: "session-recap" }, { kind: "open-quests" }]
      : [{ kind: "decided-this-week" }, { kind: "open-actions" }]

  const [latest, next] = cleanTitles(recentTitles)
  if (latest) questions.push({ kind: "summarise", title: latest })
  if (next) questions.push({ kind: "came-up", title: next })
  return questions
}
